import { useContext, useState } from 'react';
import { Input } from 'antd';
import { getErrorMessages } from '../../../api/utils/util-func';
import {
  deleteStudentClassCode,
  putStudentClassCode,
} from '../../../api/utils/student/profile/apis';
import HFbutton from '../../common/input/HFbutton';
import TextWinds from '../../common/TextWinds';
import { Field } from '../../common/form/Field';
import SectionHeader, {
  SectionBody,
  SectionControlProps,
} from '../../common/form/SectionHeader';
import {
  classInfo,
  CLASS_TYPE_LABEL,
  ClassType,
} from '../../../constants/types/student-type';
import { StudentProfile } from '../../../constants/types/student/profile/type';
import { openAlert, openConfirm } from '../../../store/features/modalSlice';
import { useAppDispatch } from '../../../store/hook';
import { StudentProfileContext } from '../../../pages/student/StudentProfileContext';

/** 등록할 수 있는 클래스 코드 개수 */
export const MAX_CLASS_LIMIT = 3;

/** 목록 한 줄에 쓰는 값. 클래스 상세를 주지 않는 응답도 코드만으로 다룬다. */
type ClassRow = Pick<classInfo, 'classId'> &
  Partial<Pick<classInfo, 'classType' | 'teacherName'>>;

/** 어떤 클래스인지 알아볼 수 있는 이름. 타입을 모르면 코드만 보여준다. */
const classTypeLabel = (classType?: ClassType) =>
  (classType && CLASS_TYPE_LABEL[classType]) || '클래스';

/**
 * 내 정보 > 클래스 정보 영역.
 * 추가/삭제가 그 자리에서 바로 반영되므로 저장 버튼 대신 '완료'로 수정 상태만 끝낸다.
 */
const ClassCodeSection = ({ section }: { section: SectionControlProps }) => {
  const dispatch = useAppDispatch();
  const { studentProfile, setStudentProfile } = useContext(
    StudentProfileContext,
  );
  const [classCode, setClassCode] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = section.isEditing;
  /** 클래스 상세를 주지 않는 응답에서는 코드만 보여준다. */
  const classList: ClassRow[] = studentProfile?.classInfos?.length
    ? studentProfile.classInfos
    : (studentProfile?.classIds ?? []).map((classId) => ({ classId }));

  /** 목록이 바뀌면 다른 영역도 같은 값을 보도록 컨텍스트를 갱신한다. */
  const applyClassList = (
    updated: Pick<StudentProfile, 'classIds' | 'classInfos'>,
  ) => {
    if (!studentProfile || !updated) return;
    // 클래스 코드 API 응답에는 보호자 등 다른 영역 값이 없으므로 클래스 목록만 반영한다.
    setStudentProfile({
      ...studentProfile,
      classIds: updated.classIds ?? [],
      classInfos: updated.classInfos ?? [],
    });
  };

  const addClassCode = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      applyClassList(await putStudentClassCode(classCode));
      setClassCode('');
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsSaving(false);
    }
  };

  const deleteClassCode = async (code: string) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      applyClassList(await deleteStudentClassCode(code));
    } catch (error) {
      dispatch(
        openAlert({
          message: '클래스 삭제 실패. 새로고침 후 다시 시도해 주세요.',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteHandler = (
    className: string,
    teacherName: string,
    code: string,
  ) => {
    dispatch(
      openConfirm({
        message: `${className} (${teacherName} 선생님) 클래스를 정말로 삭제하시겠습니까?`,
        onConfirm: () => deleteClassCode(code),
        confirmText: '삭제하기',
      }),
    );
  };

  /** 수정을 끝낼 때는 입력하던 코드를 비운다. */
  const close = () => {
    setClassCode('');
    section.onClose();
  };

  return (
    <section>
      <SectionHeader
        title="클래스 정보"
        isEditing={isEditing}
        disabled={section.disabled}
        isSaving={isSaving}
        onEdit={section.onEdit}
        onCancel={close}
        onSave={close}
        isOpen={section.isOpen}
        onToggle={section.onToggle}
        // 추가/삭제가 즉시 반영되므로 되돌릴 값이 없다.
        editLabel="관리하기"
        saveLabel="관리 끝내기"
        hideCancel
      />

      <SectionBody isOpen={section.isOpen}>
        <div className="mt-5 flex flex-col gap-y-2.5 pb-2">
          <TextWinds type="content_body3" color="gray7">
            클래스는 최대 {MAX_CLASS_LIMIT}개까지 등록할 수 있어요.
            <br />
            클래스는 유형별로 하나만 등록할 수 있어요.
          </TextWinds>

          {classList.length === 0 && !isEditing ? (
            <TextWinds type="content_body2" className="m-auto mb-4 text-gray6">
              등록된 클래스가 없습니다
            </TextWinds>
          ) : (
            classList.map(({ classId, classType, teacherName }) => (
              <div key={classId} className="flex items-center gap-2">
                <div className="flex min-w-0 flex-1 flex-col justify-center rounded border border-gray3 bg-gray-50 px-3 py-1.5">
                  <TextWinds type="content_body2" color="gray8">
                    {classTypeLabel(classType)}
                  </TextWinds>
                  <TextWinds type="caption1" color="gray7">
                    {teacherName && `${teacherName} 선생님 · `}
                    클래스 코드 {classId}
                  </TextWinds>
                </div>

                {isEditing && (
                  <HFbutton
                    type="Outlined"
                    width="auto"
                    // 추가/삭제 요청이 겹치면 목록이 어긋나므로 처리 중에는 막는다.
                    disabled={isSaving}
                    onClick={() =>
                      deleteHandler(
                        classTypeLabel(classType),
                        teacherName,
                        classId,
                      )
                    }
                  >
                    삭제
                  </HFbutton>
                )}
              </div>
            ))
          )}

          {isEditing && classList.length < MAX_CLASS_LIMIT && (
            <Field label="클래스 코드 추가" className="mt-2">
              <div className="flex items-center gap-2">
                <Input
                  size="large"
                  className="min-w-0 flex-1 rounded p-3"
                  placeholder="클래스 코드 입력"
                  value={classCode}
                  onChange={(e) => setClassCode(e.target.value)}
                />
                <HFbutton
                  height={50}
                  width="auto"
                  onClick={addClassCode}
                  disabled={classCode === '' || isSaving}
                >
                  추가
                </HFbutton>
              </div>
            </Field>
          )}
        </div>
      </SectionBody>
    </section>
  );
};

export default ClassCodeSection;
